import React, { useState, useEffect } from "react";
import {
  Box, Paper, Typography, Button, Stepper, Step, StepLabel,
  Dialog, DialogTitle, DialogContent, DialogActions, Snackbar, Alert,
  TextField, Grid, IconButton, FormControl, InputLabel, Select,
  MenuItem, Autocomplete, Divider, InputAdornment, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow
} from "@mui/material";
import { Add, Delete, Edit, Refresh } from "@mui/icons-material";
import axios from "axios";
import PasoConfirmarCompra from "./compras/pasos/PasoConfirmarCompra";
import PasoExito from "./compras/pasos/PasoExito";
import TablaCompras from "./compras/TablaCompras";

const API_URL = process.env.REACT_APP_API_URL || "";

const steps = ["Datos Proveedor y Factura", "Confirmar Compra", "Completado"];

export default function ComprasManual() {
  const [compras, setCompras] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [proveedores, setProveedores] = useState([]);
  const [productos, setProductos] = useState([]);
  const [cuentas, setCuentas] = useState([]);
  const [compraCreada, setCompraCreada] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  // Datos del formulario manual (Paso 0)
  const [proveedor, setProveedor] = useState({ ruc: "", razonSocial: "", direccion: "", email: "" });
  const [factura, setFactura] = useState({ numeroComprobante: "", fechaEmision: new Date().toISOString().split("T")[0], claveAcceso: "" });
  const [detalles, setDetalles] = useState([]);

  const getAuthHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

  useEffect(() => { cargarCompras(); cargarProveedores(); cargarProductos(); cargarCuentas(); }, []);

  const cargarCompras = async () => {
    setLoading(true);
    try { const res = await axios.get(`${API_URL}/api/contabilidad/compras`, { headers: getAuthHeaders() }); setCompras(res.data); }
    catch { }
    setLoading(false);
  };

  const cargarProveedores = async () => {
    try { const res = await axios.get(`${API_URL}/api/proveedores?activo=true`, { headers: getAuthHeaders() }); setProveedores(res.data || []); }
    catch { }
  };

  const cargarProductos = async () => {
    try { const res = await axios.get(`${API_URL}/api/productos`, { headers: getAuthHeaders() }); setProductos(Array.isArray(res.data) ? res.data : []); }
    catch { setProductos([]); }
  };

  const cargarCuentas = async () => {
    try { const res = await axios.get(`${API_URL}/api/contabilidad/plan-cuentas`, { headers: getAuthHeaders() }); setCuentas(res.data.filter(c => c.acepta_movimiento)); }
    catch { }
  };

  const showSnackbar = (msg, sev) => setSnackbar({ open: true, message: msg, severity: sev });

  // ── Proveedor ──
  const seleccionarProveedor = (prov) => {
    if (!prov) return;
    setProveedor({
      ruc: prov.ruc_cedula || prov.ruc || "",
      razonSocial: prov.razon_social || prov.nombre || "",
      direccion: prov.direccion_matriz || prov.direccion || "",
      email: (prov.emails && prov.emails[0]) || prov.email || "",
    });
  };

  // ── Detalles ──
  const agregarDetalle = () => {
    setDetalles([...detalles, { id: Date.now(), codigoPrincipal: "", descripcion: "", cantidad: 1, precioUnitario: 0, descuento: 0, codigoPorcentaje: "4" }]);
  };

  const actualizarDetalle = (id, campo, valor) => {
    setDetalles(detalles.map(d => d.id === id ? { ...d, [campo]: valor } : d));
  };

  const eliminarDetalle = (id) => setDetalles(detalles.filter(d => d.id !== id));

  const vincularProducto = (detalleId, producto) => {
    if (!producto) return;
    setDetalles(detalles.map(d => d.id === detalleId ? {
      ...d, descripcion: producto.nombre, precioUnitario: producto.costo_ultima_compra || producto.precio_1 || 0,
      codigoPrincipal: producto.codigo || "",
    } : d));
  };

  // ── Totales ──
  const calcularTotales = () => {
    let subtotal0 = 0, subtotal15 = 0, iva15 = 0;
    detalles.forEach(d => {
      const sub = (parseFloat(d.cantidad) || 0) * (parseFloat(d.precioUnitario) || 0) - (parseFloat(d.descuento) || 0);
      if (d.codigoPorcentaje === "0" || d.codigoPorcentaje === "6") subtotal0 += sub;
      else { subtotal15 += sub; iva15 += sub * 0.15; }
    });
    return { subtotal0: +subtotal0.toFixed(2), subtotal15: +subtotal15.toFixed(2), iva15: +iva15.toFixed(2), importeTotal: +(subtotal0 + subtotal15 + iva15).toFixed(2), totalSinImpuestos: +(subtotal0 + subtotal15).toFixed(2), totalDescuento: +detalles.reduce((s, d) => s + (parseFloat(d.descuento) || 0), 0).toFixed(2) };
  };

  // ── Construir objeto "factura parseada" para pasar al PasoConfirmarCompra ──
  const construirFacturaParseada = () => {
    const tots = calcularTotales();
    return {
      proveedor,
      factura: {
        ...factura,
        establecimiento: factura.numeroComprobante.split("-")[0] || "001",
        puntoEmision: factura.numeroComprobante.split("-")[1] || "001",
        secuencial: factura.numeroComprobante.split("-")[2] || "000000001",
      },
      totales: tots,
      productos: detalles.map(d => {
        const sub = (parseFloat(d.cantidad) || 0) * (parseFloat(d.precioUnitario) || 0) - (parseFloat(d.descuento) || 0);
        const tarifa = (d.codigoPorcentaje === "0" || d.codigoPorcentaje === "6") ? 0 : 15;
        return {
          codigoPrincipal: d.codigoPrincipal || "",
          descripcion: d.descripcion,
          cantidad: parseFloat(d.cantidad) || 0,
          precioUnitario: parseFloat(d.precioUnitario) || 0,
          descuento: parseFloat(d.descuento) || 0,
          precioTotalSinImpuesto: sub.toFixed(2),
          impuestos: [{ codigo: "2", codigoPorcentaje: d.codigoPorcentaje, tarifa, baseImponible: sub, valor: sub * tarifa / 100 }],
        };
      }),
    };
  };

  // ── Confirmar compra (callback del PasoConfirmarCompra) ──
  const handleConfirmarCompra = async (datosCompra) => {
    setLoading(true);
    try {
      const payload = { facturaData: construirFacturaParseada(), ...datosCompra, permitirDuplicado: false };
      const res = await axios.post(`${API_URL}/api/contabilidad/compras/con-asiento`, payload, { headers: getAuthHeaders() });
      setCompraCreada(res.data.compra);
      setActiveStep(2);
      showSnackbar(res.data.message || "Compra registrada exitosamente", "success");
      cargarCompras();
    } catch (err) {
      if (err.response?.status === 409) {
        if (window.confirm(`${err.response.data.error}\n\nRegistrar de todas formas?`)) {
          try {
            const payload2 = { facturaData: construirFacturaParseada(), ...datosCompra, permitirDuplicado: true };
            const res = await axios.post(`${API_URL}/api/contabilidad/compras/con-asiento`, payload2, { headers: getAuthHeaders() });
            setCompraCreada(res.data.compra); setActiveStep(2); showSnackbar("Compra registrada", "success"); cargarCompras();
          } catch { showSnackbar("Error al registrar", "error"); }
        }
      } else { showSnackbar(err.response?.data?.error || "Error al registrar compra", "error"); }
    }
    setLoading(false);
  };

  // ── Reset ──
  const resetForm = () => {
    setActiveStep(0);
    setProveedor({ ruc: "", razonSocial: "", direccion: "", email: "" });
    setFactura({ numeroComprobante: "", fechaEmision: new Date().toISOString().split("T")[0], claveAcceso: "" });
    setDetalles([]);
    setCompraCreada(null);
  };

  // ── RENDER ──
  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Typography variant="h4" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Edit /> Compras Manuales
        </Typography>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Button variant="outlined" startIcon={<Refresh />} onClick={cargarCompras} disabled={loading}>Actualizar</Button>
          <Button variant="contained" color="success" startIcon={<Add />} onClick={() => { resetForm(); setDialogOpen(true); }} size="large">
            Nueva Compra Manual
          </Button>
        </Box>
      </Box>

      <Paper sx={{ p: 3 }}>
        <TablaCompras compras={compras} loading={loading} onRecargar={cargarCompras} />
      </Paper>

      {/* Dialog */}
      <Dialog open={dialogOpen} onClose={() => !loading && setDialogOpen(false)} maxWidth="xl" fullWidth PaperProps={{ sx: { minHeight: "80vh" } }}>
        <DialogTitle><Typography variant="h5">Registrar Compra Manual</Typography></DialogTitle>
        <DialogContent dividers sx={{ px: 4 }}>
          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map(l => <Step key={l}><StepLabel>{l}</StepLabel></Step>)}
          </Stepper>

          {/* PASO 0: Proveedor + Factura + Items */}
          {activeStep === 0 && (
            <Box>
              <Typography variant="h6" gutterBottom color="primary">Proveedor</Typography>
              <Autocomplete
                options={proveedores}
                getOptionLabel={(o) => `${o.ruc_cedula || o.ruc || ""} - ${o.razon_social || o.nombre || ""}`}
                onChange={(_, v) => v && seleccionarProveedor(v)}
                renderInput={(params) => <TextField {...params} label="Buscar proveedor existente" placeholder="RUC o nombre..." />}
                sx={{ mb: 2 }}
              />
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={4}><TextField fullWidth label="RUC / Cedula *" value={proveedor.ruc} onChange={e => setProveedor({ ...proveedor, ruc: e.target.value })} /></Grid>
                <Grid item xs={12} sm={8}><TextField fullWidth label="Razon Social *" value={proveedor.razonSocial} onChange={e => setProveedor({ ...proveedor, razonSocial: e.target.value })} /></Grid>
                <Grid item xs={12} sm={6}><TextField fullWidth label="Direccion" value={proveedor.direccion} onChange={e => setProveedor({ ...proveedor, direccion: e.target.value })} /></Grid>
                <Grid item xs={12} sm={6}><TextField fullWidth label="Email" value={proveedor.email} onChange={e => setProveedor({ ...proveedor, email: e.target.value })} /></Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />
              <Typography variant="h6" gutterBottom color="primary">Factura</Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={4}><TextField fullWidth label="No. Comprobante *" value={factura.numeroComprobante} onChange={e => setFactura({ ...factura, numeroComprobante: e.target.value })} placeholder="001-001-000000001" /></Grid>
                <Grid item xs={12} sm={4}><TextField fullWidth type="date" label="Fecha Emision *" value={factura.fechaEmision} onChange={e => setFactura({ ...factura, fechaEmision: e.target.value })} InputLabelProps={{ shrink: true }} /></Grid>
                <Grid item xs={12} sm={4}><TextField fullWidth label="Clave Acceso (opcional)" value={factura.claveAcceso} onChange={e => setFactura({ ...factura, claveAcceso: e.target.value })} placeholder="49 digitos" /></Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Typography variant="h6" color="primary">Items</Typography>
                <Button startIcon={<Add />} onClick={agregarDetalle} variant="outlined">Agregar item</Button>
              </Box>

              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: "#f5f5f5" }}>
                      <TableCell><strong>Descripcion</strong></TableCell>
                      <TableCell sx={{ width: 160 }}><strong>Producto (vincular)</strong></TableCell>
                      <TableCell sx={{ width: 80 }}><strong>Cant.</strong></TableCell>
                      <TableCell sx={{ width: 100 }}><strong>P. Unit.</strong></TableCell>
                      <TableCell sx={{ width: 80 }}><strong>Desc.</strong></TableCell>
                      <TableCell sx={{ width: 90 }}><strong>IVA</strong></TableCell>
                      <TableCell sx={{ width: 90 }} align="right"><strong>Subtotal</strong></TableCell>
                      <TableCell sx={{ width: 40 }} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {detalles.length === 0 ? (
                      <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">Agregue productos o servicios</Typography>
                        <Button startIcon={<Add />} onClick={agregarDetalle} sx={{ mt: 1 }}>Agregar primer item</Button>
                      </TableCell></TableRow>
                    ) : detalles.map(d => {
                      const sub = (parseFloat(d.cantidad) || 0) * (parseFloat(d.precioUnitario) || 0) - (parseFloat(d.descuento) || 0);
                      return (
                        <TableRow key={d.id}>
                          <TableCell><TextField size="small" fullWidth value={d.descripcion} onChange={e => actualizarDetalle(d.id, "descripcion", e.target.value)} placeholder="Descripcion..." /></TableCell>
                          <TableCell>
                            <Autocomplete size="small" options={productos} getOptionLabel={o => `${o.codigo || ""} ${o.nombre || ""}`}
                              onChange={(_, v) => vincularProducto(d.id, v)} renderInput={params => <TextField {...params} placeholder="Buscar..." />} />
                          </TableCell>
                          <TableCell><TextField size="small" type="number" value={d.cantidad || ""} onChange={e => actualizarDetalle(d.id, "cantidad", e.target.value)} onFocus={e => { if (+e.target.value === 0) e.target.value = ""; }} inputProps={{ min: 0, step: 0.01 }} sx={{ width: 70 }} /></TableCell>
                          <TableCell><TextField size="small" type="number" value={d.precioUnitario || ""} onChange={e => actualizarDetalle(d.id, "precioUnitario", e.target.value)} onFocus={e => { if (+e.target.value === 0) e.target.value = ""; }} InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} sx={{ width: 100 }} /></TableCell>
                          <TableCell><TextField size="small" type="number" value={d.descuento || ""} onChange={e => actualizarDetalle(d.id, "descuento", e.target.value)} onFocus={e => { if (+e.target.value === 0) e.target.value = ""; }} sx={{ width: 70 }} /></TableCell>
                          <TableCell><Select size="small" value={d.codigoPorcentaje} onChange={e => actualizarDetalle(d.id, "codigoPorcentaje", e.target.value)}>
                            <MenuItem value="4">15%</MenuItem><MenuItem value="0">0%</MenuItem><MenuItem value="6">No obj.</MenuItem>
                          </Select></TableCell>
                          <TableCell align="right"><strong>${sub.toFixed(2)}</strong></TableCell>
                          <TableCell><IconButton size="small" color="error" onClick={() => eliminarDetalle(d.id)}><Delete fontSize="small" /></IconButton></TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              {detalles.length > 0 && (
                <Paper variant="outlined" sx={{ p: 2, mt: 2, bgcolor: "#f9f9f9" }}>
                  <Grid container spacing={2} justifyContent="flex-end">
                    <Grid item xs={3}><Typography variant="caption" color="text.secondary">Subtotal 0%</Typography><Typography fontWeight={600}>${calcularTotales().subtotal0}</Typography></Grid>
                    <Grid item xs={3}><Typography variant="caption" color="text.secondary">Subtotal 15%</Typography><Typography fontWeight={600}>${calcularTotales().subtotal15}</Typography></Grid>
                    <Grid item xs={3}><Typography variant="caption" color="text.secondary">IVA 15%</Typography><Typography fontWeight={600}>${calcularTotales().iva15}</Typography></Grid>
                    <Grid item xs={3}><Typography variant="caption" color="text.secondary">TOTAL</Typography><Typography variant="h6" fontWeight={700} color="primary">${calcularTotales().importeTotal}</Typography></Grid>
                  </Grid>
                </Paper>
              )}

              <Box sx={{ mt: 3, textAlign: "right" }}>
                <Button variant="contained" size="large" onClick={() => {
                  if (!proveedor.ruc || !proveedor.razonSocial) return showSnackbar("Complete RUC y Razon Social", "error");
                  if (!factura.numeroComprobante) return showSnackbar("Ingrese el numero de comprobante", "error");
                  if (detalles.length === 0) return showSnackbar("Agregue al menos un item", "error");
                  setActiveStep(1);
                }}>
                  Siguiente — Confirmar Compra
                </Button>
              </Box>
            </Box>
          )}

          {/* PASO 1: Confirmar (usa el mismo componente que XML) */}
          {activeStep === 1 && (
            <PasoConfirmarCompra
              factura={construirFacturaParseada()}
              retenciones={null}
              cuentas={cuentas}
              productos={productos}
              onConfirmar={handleConfirmarCompra}
              onCancelar={() => setActiveStep(0)}
              loading={loading}
            />
          )}

          {/* PASO 2: Exito */}
          {activeStep === 2 && (
            <PasoExito
              compra={compraCreada}
              onFinalizar={() => { setDialogOpen(false); resetForm(); }}
              onNuevaCompra={() => resetForm()}
            />
          )}
        </DialogContent>

        {activeStep < 2 && (
          <DialogActions><Button onClick={() => setDialogOpen(false)} disabled={loading}>Cancelar</Button></DialogActions>
        )}
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}
